import { ActionIcon, Autocomplete, Loader, Modal, Select, TextInput } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import type { CoverageEligibilityResponse } from '@medplum/fhirtypes'
import { IconCirclePlus, IconSearch, IconTrash, IconUpload, IconX } from '@tabler/icons-react'
import type { ChangeEvent, RefObject } from 'react'
import {
  ACCEPTED_IMAGE_TYPES,
  COVERAGE_TYPES,
  INSURANCE_CARD_DISPLAY,
  INSURANCE_CARD_SIDES,
  INSURANCE_RANKS,
} from '@/constants/insuranceConstants'
import type { InsuranceCardSide, InsuranceFormData, InsuranceRank } from '@/types/insuranceTypes'
import type { EditableCoverage } from './manageCoverages.types'
import { CoverageEligibilityOutcomePanel } from '@/components/ui'
import { InsurancePlansDropdown } from './InsurancePlansDropdown'
import type { InsurancePayerOption } from './manageCoverages.types'
import type { PatientInsuranceRequest } from '@/utils/insuranceQuestionnaire'
import { PatientInsuranceRequestPanel } from '@/components/ui/PatientInsuranceRequestPanel'
import { USDateInput } from '@/components/ui/USDateInput'
import { FormSectionHeader } from '@/components/ui/FormSectionHeader'
import {
  CANCEL_BTN,
  FIELD_CLASSES,
  FIELD_LABEL,
  FOOTER_HINT,
  GHOST_BTN,
  MODAL_CONTENT,
  MODAL_FOOTER,
  SECONDARY_BTN,
  SUBMIT_BTN,
} from '@/components/ui/formModal.tokens'

type ManageCoveragesEditorModalProps = {
  editingCoverage: EditableCoverage
  form: UseFormReturnType<InsuranceFormData>
  frontInputRef: RefObject<HTMLInputElement | null>
  backInputRef: RefObject<HTMLInputElement | null>
  frontPreviewUrl: string | null
  backPreviewUrl: string | null
  frontHasPersistedCard: boolean
  backHasPersistedCard: boolean
  hasAnyInsurancePreview: boolean
  hasNewInsuranceCardSelection: boolean
  isEligibilityCheck: boolean
  loading: boolean
  opened: boolean
  organizationOptions: InsurancePayerOption[]
  filteredOptions: string[]
  searchValue: string
  isCoverageStateLocked: boolean
  insurancePlansStateProp: string | null
  latestEligibilityResponse: CoverageEligibilityResponse | null
  isEligibilityStale: boolean
  patientInsuranceRequest: PatientInsuranceRequest | null
  patientId?: string
  onPatientInsuranceRequestDismissed?: () => void
  onClose: () => void
  onSubmit: (values: InsuranceFormData) => Promise<void>
  /** Saves the insurance, then closes `patientInsuranceRequest` (only offered while one is shown). */
  onSubmitAndCloseRequest?: (values: InsuranceFormData) => Promise<void>
  onSearchChange: (value: string) => void
  onPayerSelected: (option: InsurancePayerOption) => void
  onOpenCreatePayer: () => void
  onPlanChange: (
    value: {
      planName: string
      productType: string
      planId: string
      lobCategory: string
    } | null,
  ) => void
  onCoverageStateChange: (state: string | null) => void
  onStartInsuranceSelection: () => void
  onRetakeImage: (side: InsuranceCardSide) => void
  onDeleteImage: (side: InsuranceCardSide) => void
  onFrontInputChange: (event: ChangeEvent<HTMLInputElement>) => void
  onBackInputChange: (event: ChangeEvent<HTMLInputElement>) => void
  onDropImage: (side: InsuranceCardSide, file: File | null) => void
  onZoomImage: (url: string | null) => void
  onResetInsuranceUploads: () => void
  onCheckInsurance: () => void
}

const normalize = (value?: string) => value?.trim().toLowerCase() ?? ''

/**
 * True when the plan in the form is the one the patient asked for: same member ID and payer. A
 * request without a catalog payer id only has the name the patient typed, so any chosen payer
 * passes here and staff confirm the pairing before anything is saved.
 */
function planMatchesRequest(values: InsuranceFormData, request: PatientInsuranceRequest) {
  // A request filed without a member ID matches on the insurer once staff enter the ID.
  const memberId = normalize(request.insuranceNumber)
  if (memberId ? normalize(values.memberId) !== memberId : !normalize(values.memberId)) {
    return false
  }
  return request.payerId
    ? normalize(values.payorId) === normalize(request.payerId)
    : Boolean(normalize(values.payorId))
}

export function ManageCoveragesEditorModal({
  editingCoverage,
  form,
  frontInputRef,
  backInputRef,
  frontPreviewUrl,
  backPreviewUrl,
  frontHasPersistedCard,
  backHasPersistedCard,
  hasAnyInsurancePreview,
  hasNewInsuranceCardSelection,
  isEligibilityCheck,
  loading,
  opened,
  organizationOptions,
  filteredOptions,
  searchValue,
  isCoverageStateLocked,
  insurancePlansStateProp,
  latestEligibilityResponse,
  isEligibilityStale,
  patientInsuranceRequest,
  patientId,
  onPatientInsuranceRequestDismissed,
  onClose,
  onSubmit,
  onSubmitAndCloseRequest,
  onSearchChange,
  onPayerSelected,
  onOpenCreatePayer,
  onPlanChange,
  onCoverageStateChange,
  onStartInsuranceSelection,
  onRetakeImage,
  onDeleteImage,
  onFrontInputChange,
  onBackInputChange,
  onDropImage,
  onZoomImage,
  onResetInsuranceUploads,
  onCheckInsurance,
}: ManageCoveragesEditorModalProps) {
  const canCheckEligibility = Boolean(patientId && editingCoverage?.type === 'existing')
  const canCloseRequest = Boolean(patientId && patientInsuranceRequest && onSubmitAndCloseRequest)
  const planMatches = Boolean(
    patientInsuranceRequest && planMatchesRequest(form.values, patientInsuranceRequest),
  )
  const required = <span className="text-[#E5533D]">*</span>

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      withCloseButton={false}
      classNames={{ content: MODAL_CONTENT, body: 'p-0' }}
      radius={18}
      size="760px"
    >
      <form
        onSubmit={(event) => {
          event.stopPropagation()
          form.onSubmit(onSubmit)(event)
        }}
        className="flex max-h-[86vh] flex-col"
      >
        {/* Header — pinned above the scrolling body. */}
        <div className="flex items-start gap-2.5 px-6 pt-5 pb-4">
          <div className="flex-1">
            <div className="text-[17px] font-extrabold tracking-tight text-[#1F2733]">
              {editingCoverage ? 'Edit insurance' : 'Create new insurance'}
            </div>
            <div className="text-[12.5px] text-[#8A929C]">
              Required fields are marked {required}
            </div>
          </div>
          <button
            type="button"
            title="Close"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-[7px] text-[#8A929C] hover:bg-[#F2F4F6] hover:text-[#1F2733]"
          >
            <IconX size={17} stroke={2.1} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto border-t border-[#EEF0F2] px-6 py-5">
          <CoverageEligibilityOutcomePanel
            latestEligibilityResponse={latestEligibilityResponse}
            isEligibilityStale={isEligibilityStale}
            className="border-grey mb-4 max-h-100 overflow-y-auto rounded-md border border-purple-800 bg-purple-50 p-4"
          />

          <PatientInsuranceRequestPanel
            request={patientInsuranceRequest}
            onDismissed={onPatientInsuranceRequestDismissed}
            tone="compact"
          />

          <FormSectionHeader title="Provider & plan" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Autocomplete
              label="Insurance provider"
              placeholder="Search for insurance provider..."
              data={filteredOptions}
              value={searchValue}
              classNames={FIELD_CLASSES}
              leftSection={<IconSearch size={16} className="text-[#9AA0A8]" />}
              onChange={(value) => {
                onSearchChange(value)
                const selectedOption = organizationOptions.find((option) => option.label === value)
                if (selectedOption) {
                  onPayerSelected(selectedOption)
                }
              }}
              onOptionSubmit={(value) => {
                const selectedOption = organizationOptions.find((option) => option.label === value)
                if (selectedOption) {
                  onSearchChange(selectedOption.label)
                  onPayerSelected(selectedOption)
                }
              }}
              error={form.errors.payorId}
              required
              rightSection={
                searchValue ? (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    aria-label="Clear search"
                    onClick={() => {
                      onSearchChange('')
                      form.setFieldValue('payorId', '')
                    }}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : null
              }
              rightSectionPointerEvents={searchValue ? 'all' : 'none'}
            />
            <TextInput
              label="Payer ID"
              placeholder="Payer ID"
              required
              classNames={FIELD_CLASSES}
              {...form.getInputProps('payorId')}
            />
          </div>

          <div className="mt-4">
            <InsurancePlansDropdown
              payerId={form.values.payorId || undefined}
              state={insurancePlansStateProp}
              value={
                form.values.planId ||
                form.values.planName ||
                form.values.productType ||
                form.values.lobCategory
                  ? {
                      planName: form.values.planName || '',
                      productType: form.values.productType || '',
                      planId: form.values.planId || '',
                      lobCategory: form.values.lobCategory || '',
                    }
                  : null
              }
              onChange={onPlanChange}
              onStateChange={onCoverageStateChange}
              disabled={loading}
            />
          </div>

          {isCoverageStateLocked && form.values.coverageState ? (
            <div className="mt-4">
              <TextInput
                label="Coverage state"
                value={form.values.coverageState}
                readOnly
                disabled
                classNames={FIELD_CLASSES}
              />
            </div>
          ) : null}

          <button type="button" className={`mt-3 ${GHOST_BTN}`} onClick={onOpenCreatePayer}>
            <IconCirclePlus size={16} />
            Can&apos;t find the provider or plan? Add details
          </button>

          <div className="mt-6">
            <FormSectionHeader title="Member details" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextInput
                label="Member ID"
                placeholder="Member ID"
                classNames={FIELD_CLASSES}
                {...form.getInputProps('memberId')}
              />
              <TextInput
                label="Group ID"
                placeholder="Group ID"
                classNames={FIELD_CLASSES}
                {...form.getInputProps('groupId')}
              />
            </div>

            {/* Rank is a three-way choice, so it reads better as a segmented
                control than a dropdown. */}
            <div className="mt-4">
              <p className={FIELD_LABEL}>Insurance rank {required}</p>
              <div
                role="radiogroup"
                aria-label="Insurance rank"
                className="inline-flex items-center gap-1 rounded-lg bg-[#F1F2F4] p-1"
              >
                {INSURANCE_RANKS.map((rank) => {
                  const selected = form.values.insuranceRank === rank.value
                  return (
                    <button
                      key={rank.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() =>
                        form.setFieldValue('insuranceRank', rank.value as InsuranceRank)
                      }
                      className={`cursor-pointer rounded-md px-4 py-[6px] text-[13px] font-bold transition ${
                        selected
                          ? 'border border-[#E2E5E9] bg-white text-[#1F2733] shadow-[0_1px_2px_rgba(16,24,40,0.06)]'
                          : 'border border-transparent text-[#6B7280] hover:text-[#1F2733]'
                      }`}
                    >
                      {rank.label}
                    </button>
                  )
                })}
              </div>
              {form.errors.insuranceRank && (
                <p className="mt-1 text-[12px] text-[#E5533D]">{form.errors.insuranceRank}</p>
              )}
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Select
                required
                label="Coverage type"
                placeholder="Select coverage type"
                data={COVERAGE_TYPES}
                classNames={FIELD_CLASSES}
                {...form.getInputProps('coverageType')}
              />
            </div>

            {form.values.coverageType && form.values.coverageType.toLowerCase() !== 'self' && (
              <div className="mt-6">
                <FormSectionHeader title="Subscriber information" />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <TextInput
                    label="Subscriber name"
                    placeholder="Full name of policy holder"
                    classNames={FIELD_CLASSES}
                    {...form.getInputProps('subscriberName')}
                  />
                  <USDateInput
                    label="Subscriber DOB"
                    value={form.values.subscriberDob || null}
                    onChange={(value) => form.setFieldValue('subscriberDob', value ?? '')}
                    onBlur={() => form.validateField('subscriberDob')}
                    error={form.errors.subscriberDob}
                    classNames={FIELD_CLASSES}
                  />
                  <TextInput
                    label="Subscriber member ID"
                    placeholder="Policy holder's member ID"
                    classNames={FIELD_CLASSES}
                    {...form.getInputProps('subscriberMemberId')}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="mt-6">
            <FormSectionHeader title="Card images">
              <button
                type="button"
                onClick={onStartInsuranceSelection}
                className="shrink-0 cursor-pointer rounded-lg border border-[#D8DBE0] bg-white px-3 py-[5px] text-[12px] font-bold text-[#33383F] hover:bg-[#F7F8FA]"
              >
                Upload insurance
              </button>
            </FormSectionHeader>
            <p className="mb-3 text-[12.5px] text-[#8A929C]">
              Click a slot or drag &amp; drop a photo. JPG or PNG.
            </p>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {INSURANCE_CARD_SIDES.map((side) => {
                const previewUrl = side === 'front' ? frontPreviewUrl : backPreviewUrl
                const hasImage = Boolean(previewUrl)
                const hasPersistedCard =
                  side === 'front' ? frontHasPersistedCard : backHasPersistedCard
                const hasCardOnFile = hasImage || hasPersistedCard
                const display = INSURANCE_CARD_DISPLAY[side]

                return (
                  <div key={side} className="flex flex-col gap-1.5">
                    <div className="flex items-baseline justify-between">
                      <span className="text-[13px] font-bold text-[#1F2733]">{display.label}</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onRetakeImage(side)}
                          className="cursor-pointer text-[12px] font-bold text-[#E5533D] hover:underline"
                        >
                          {hasCardOnFile ? 'Replace' : 'Upload'}
                        </button>
                        {hasCardOnFile ? (
                          <button
                            type="button"
                            title={`Delete ${display.label.toLowerCase()}`}
                            aria-label={`Delete ${display.label}`}
                            onClick={() => onDeleteImage(side)}
                            className="cursor-pointer text-[#8A929C] hover:text-[#E5533D]"
                          >
                            <IconTrash size={14} stroke={2} />
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (previewUrl) {
                          onZoomImage(previewUrl)
                        } else {
                          onRetakeImage(side)
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          if (previewUrl) {
                            onZoomImage(previewUrl)
                          } else {
                            onRetakeImage(side)
                          }
                        }
                      }}
                      onDrop={(event) => {
                        event.preventDefault()
                        const file = event.dataTransfer.files?.[0] ?? null
                        onDropImage(side, file)
                      }}
                      onDragOver={(event) => event.preventDefault()}
                      className="flex min-h-[150px] cursor-pointer items-center justify-center rounded-xl border border-dashed border-[#D8DBE0] bg-[#FAFBFC] p-4 text-center hover:border-[#E5533D]"
                    >
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt={`${display.label} preview`}
                          className="h-full max-h-[220px] w-full rounded-lg bg-white object-contain"
                          loading="lazy"
                        />
                      ) : hasPersistedCard ? (
                        <div className="flex flex-col items-center gap-1">
                          <IconUpload size={22} className="text-[#B0B6BE]" />
                          <span className="text-[12.5px] font-semibold text-[#6B7280]">
                            Card on file — preview unavailable
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <IconUpload size={22} className="text-[#B0B6BE]" />
                          <span className="text-[12.5px] font-semibold text-[#6B7280]">
                            {display.placeholder}
                          </span>
                          <span className="text-[11.5px] text-[#8A929C]">{display.helper}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {hasAnyInsurancePreview && hasNewInsuranceCardSelection && (
              <div className="mt-3 flex flex-col items-end gap-1">
                <button
                  type="button"
                  onClick={onResetInsuranceUploads}
                  disabled={loading}
                  className="cursor-pointer text-[12px] font-bold text-[#6B7280] hover:text-[#1F2733] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Clear
                </button>
                <span className="text-[11.5px] text-[#8A929C]">
                  New insurance photos will be uploaded when you save below.
                </span>
              </div>
            )}

            <input
              ref={frontInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES.join(',')}
              capture="environment"
              style={{ display: 'none' }}
              onChange={onFrontInputChange}
            />
            <input
              ref={backInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES.join(',')}
              capture="environment"
              style={{ display: 'none' }}
              onChange={onBackInputChange}
            />
          </div>
        </div>

        {/* Footer — pinned. */}
        <div className={MODAL_FOOTER}>
          <span className={FOOTER_HINT}>
            Card images are optional but speed up eligibility checks.
          </span>
          <button
            type="button"
            disabled={!canCheckEligibility || isEligibilityCheck}
            onClick={onCheckInsurance}
            className={SECONDARY_BTN}
          >
            {isEligibilityCheck ? (
              <span className="inline-flex items-center gap-2">
                <Loader size={13} />
                Checking...
              </span>
            ) : (
              'Check eligibility'
            )}
          </button>
          <button type="button" className={CANCEL_BTN} onClick={onClose}>
            Cancel
          </button>
          {canCloseRequest && onSubmitAndCloseRequest && (
            <button
              type="button"
              disabled={loading || !planMatches}
              title={
                planMatches ? undefined : "Enter the payer and member ID from the patient's request"
              }
              onClick={() => form.onSubmit(onSubmitAndCloseRequest)()}
              className={SECONDARY_BTN}
            >
              Add plan & close request
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            onClick={(event) => event.stopPropagation()}
            className={SUBMIT_BTN}
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <Loader size={13} color="white" />
                Saving...
              </span>
            ) : editingCoverage ? (
              'Save insurance'
            ) : (
              'Create insurance'
            )}
          </button>
        </div>
      </form>
    </Modal>
  )
}
